<template>
  <div class="grid grid-cols-5 col-span-5">
    <div v-if="message.newRequestLegend && messageIndex !== 0"
         class="col-span-5 h-0 mt-8 ml-4 border-solid border-0 border-t border-soft"/>
    <div v-if="message.newRequestLegend"
         :class="`inline-flex col-span-5 mt-${messageIndex === 0 ? '5' : '8'} pl-4 border-solid border-0 border-l-2 border-${message.borderColor}`">
      <chart-legend :type="message.newRequestLegend" :show-label="false"/>
      <span class="ml-3 text-gray-dark text-sm">{{ formatTimestamp(message.sent || message.timestamp) }}</span>
      <copyable-link v-if="getBrowserUrl(message)" :url="getBrowserUrl(message)" class="ml-3"/>
    </div>

    <vx-tooltip :text="tooltipText"
                :key="tooltipText"
                :class="`col-span-3 pt-4 pl-4 border-solid border-0 border-l-2 border-${message.borderColor}`"
                position="left"
                delay="0.7s"
    >
        <customer-bubble
          :customerInput="buttonTitle"
          :focus="false"
          :is-clickable="false"
          @context-tooltip-show="handleContextTooltipShow"
          @context-tooltip-hide="handleContextTooltipHide">
        <template #footer>
          <div class="flex items-center mt-2 text-sm text-gray-dark">
            <vs-icon icon="mouse" size="0.8rem" class="mr-1"/>
            <span v-if="type === 'postback'">
              Button clicked
            </span>
            <span v-else>
              Quick reply clicked
            </span>
          </div>
        </template>
        </customer-bubble>
    </vx-tooltip>
  </div>
</template>

<script>
import find from 'ramda/src/find'
import is from 'ramda/src/is'
import path from 'ramda/src/path'
import pathOr from 'ramda/src/pathOr'
import propEq from 'ramda/src/propEq'
import reverse from 'ramda/src/reverse'
import take from 'ramda/src/take'
import CustomerBubble from '../../../preview/v2/CustomerBubble.vue'
import ChartLegend from '@/components/analytics/charts/ChartLegend.vue'
import CopyableLink from '@/components/analytics/conversations/detail/CopyableLink.vue'
import datetimeUtils from '@/utils/datetime.utils'
import contentUtils from '@/utils/content.utils'

const MESSAGE_CONTENT_TYPE = {
  STRUCTURE: 'structure'
}

export default {
  name: 'ConversationSelectableMessage',

  components: {
    CustomerBubble,
    ChartLegend,
    CopyableLink
  },

  data() {
    return {
      contextKey: null
    }
  },

  props: {
    message: {
      type: Object,
      required: true
    },
    messageIndex: {
      type: Number,
      required: true
    },
    messages: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'postback'
    }
  },

  computed: {
    messageId() {
      return path(['data', 'message_id'], this.message)
    },

    payload() {
      return path(['data', 'payload'], this.message)
    },

    buttonTitle() {
      if (this.type === 'postback') {
        return this.findButtonTitleWithContext(this.payload)
      } else {
        return this.processButtonTitleWithContext(this.message.data.content.payload)
      }
    },

    tooltipText() {
      const timestamp = this.formatTimestamp(this.message.sent || this.message.timestamp)
      if (this.contextKey) {
        return `${timestamp}
                <div style="font-size: 0.875rem; white-space: nowrap; margin-top: 10px">
                  <b>Interpolation key:</b>
                </div>
                ${this.contextKey}
               `
      }
      return timestamp
    },

    apiCallMessage() {
      return find(msg => msg.type === 'remote_action' && msg.data.context, this.messages)
    }
  },

  methods: {
    formatTimestamp(timestamp) {
      return datetimeUtils.toMoment(timestamp).format('LL HH:mm:ss')
    },

    getBrowserUrl(message) {
      return path(['metadata', 'browserUrl'], message)
    },

    getButtonTitle() {
      return this.buttonTitle
    },

    findButtonTitle(payload) {
      if (!payload) return payload

      const previousMessages = take(this.messageIndex, this.messages)
      const reversedMessages = reverse(previousMessages)

      const messageWithButtons = find(this.isStructureMessageWithButtons, reversedMessages)

      if (!messageWithButtons) return payload

      const buttons = pathOr([], ['data', 'content', 'payload', 'buttons'], messageWithButtons)
      const matchingButton = find(propEq('payload', payload), buttons)

      return pathOr(payload, ['title'], matchingButton)
    },

    findButtonTitleWithContext(payload) {
      if (!payload) return payload

      const previousMessages = take(this.messageIndex, this.messages)
      const reversedMessages = reverse(previousMessages)

      const messageWithButtons = find(this.isStructureMessageWithButtons, reversedMessages)

      if (!messageWithButtons) return payload

      const buttons = pathOr([], ['data', 'content', 'payload', 'buttons'], messageWithButtons)
      const matchingButton = find(propEq('payload', payload), buttons)
      const originalTitle = pathOr(payload, ['title'], matchingButton)

      const originalButtons = pathOr([], ['data', 'originalContent', 'payload', 'buttons'], messageWithButtons)
      const originalMatchingButton = find(propEq('payload', payload), originalButtons)
      
      if (originalMatchingButton && originalMatchingButton.title) {
        const apiCallMessage = this.apiCallMessage
        const contextData = apiCallMessage && apiCallMessage.data.context ? apiCallMessage.data.context : null
        return contentUtils.markSubstitutedText(originalMatchingButton.title, contextData)
      }

      return originalTitle
    },

    isStructureMessageWithButtons(message) {
      return path(['data', 'content', 'type'], message) === MESSAGE_CONTENT_TYPE.STRUCTURE &&
             path(['data', 'content', 'payload', 'buttons'], message) &&
             is(Array, path(['data', 'content', 'payload', 'buttons'], message))
    },

    processButtonTitleWithContext(title) {
      const apiCallMessage = this.apiCallMessage
      const contextData = apiCallMessage && apiCallMessage.data.context ? apiCallMessage.data.context : null
      return contentUtils.markSubstitutedText(title, contextData)
    },

    handleContextTooltipShow(contextKey) {
      this.contextKey = contextKey
    },

    handleContextTooltipHide() {
      this.contextKey = null
    }

  }
}
</script>
